import { BROWSER_STATICS_FOLDER } from '@wix/cli-app-definitions';
import { mergeConfig } from 'vite';
import type { InlineConfig } from 'vite';
import { viteExternalsPlugin } from 'vite-plugin-externals';
import { isType } from 'variant';
import { resolve } from 'node:path';
import {
  virtualDashboardHTMLAssets,
  virtualDashboardScriptAssets,
} from '../../../dev-center/components/dashboard/config.js';
import type { ProjectModel } from '../../../model.js';
import { inlineHtml } from '../../plugins/inline-html.js';
import { moduleBundleBuildBrowser } from '../../plugins/module-bundle/plugin.js';
import type { ViteBuildConfigOptions } from '../../vite-config-options.js';
import { ConfigType } from '../../vite-config-options.js';
import { draftJs } from '../../plugins/draft-js.js';
import { DevCenterComponent } from '../../../dev-center/components/dev-center-component.js';
import {
  embeddedScriptSDKSupport,
  getEmbeddedScriptHtmlUrl,
} from '../../plugins/embedded-script-sdk-support.js';
import {
  virtualCustomElementPanelHTMLAssets,
  virtualCustomElementScriptAssets,
  virtualSiteComponentPanelScriptAssets,
  virtualSiteComponentPresetAssets,
} from '../../../dev-center/components/site/config.js';
import { configExtensionsFileEmitter } from '../../plugins/config-extensions-file-emitter.js';
import { hashHtmlFiles } from '../../plugins/hash-html-files.js';
import {
  preserveSiteComponentPanelsExports,
  EXPORTS_CUSTODIAN_ENTRY,
} from '../../plugins/exports-custodian.js';
import { getBaseViteConfig } from './base.js';

export function getViteConfig(
  model: ProjectModel,
  options: ViteBuildConfigOptions
): InlineConfig {
  const virtualDashboardHTMLEntries = [
    ...virtualDashboardHTMLAssets(model),
  ].map(([assetId, component]) => [`html-asset-${component.id}`, assetId]);

  const virtualDashboardScriptEntries = [
    ...virtualDashboardScriptAssets(model),
  ].map(([assetId, component]) => [`script-asset-${component.id}`, assetId]);

  const embeddedScriptEntries = model.components
    .filter(isType(DevCenterComponent.EmbeddedScript))
    .map((comp) => [comp.id, resolve(model.projectFolder, comp.relativePath)]);

  const virtualCustomElementHTMLEntries = [
    ...virtualCustomElementPanelHTMLAssets(model),
  ].map(([assetId, component]) => [`html-asset-${component.id}`, assetId]);

  const virtualCustomElementScriptEntries = [
    ...virtualCustomElementScriptAssets(model),
  ].map(([assetId, component]) => [`script-asset-${component.id}`, assetId]);

  const virtualSiteComponentPanelScriptEntries = [
    ...virtualSiteComponentPanelScriptAssets(model),
  ].map(([assetId, component]) => [`script-asset-${component.id}`, assetId]);

  const virtualSiteComponentPresetEntries = [
    ...virtualSiteComponentPresetAssets(model),
  ].map(([assetId, assetPath]) => [assetId, assetPath]);

  const baseConfig = getBaseViteConfig({
    ...options,
    model,
    outputDir: BROWSER_STATICS_FOLDER,
  });

  const config = {
    build: {
      rollupOptions: {
        /*
         * We need to preserve names of exports from our root modules for convention.
         * "strict" or "exports-only" will not work because it will create separated files with exports.
         * It will break our naive flow of extracting css, since we trying to take css from "root" module.
         */
        preserveEntrySignatures: 'allow-extension',
        input: {
          ...Object.fromEntries([
            ...virtualDashboardHTMLEntries,
            ...virtualDashboardScriptEntries,
            ...embeddedScriptEntries,
            ...virtualCustomElementHTMLEntries,
            ...virtualCustomElementScriptEntries,
            ...virtualSiteComponentPanelScriptEntries,
            ...virtualSiteComponentPresetEntries,
          ]),
          ...(virtualSiteComponentPanelScriptEntries.length
            ? { [EXPORTS_CUSTODIAN_ENTRY]: EXPORTS_CUSTODIAN_ENTRY }
            : {}),
        },
      },
    },
    plugins: [
      moduleBundleBuildBrowser({
        initialModel: model,
      }),
      viteExternalsPlugin({
        react: 'React',
        'react-dom': 'ReactDOM',
      }),
      inlineHtml({
        type: ConfigType.build,
        initialModel: model,
        ...options,
      }),
      draftJs(),
      hashHtmlFiles(),
      embeddedScriptSDKSupport({
        type: ConfigType.build,
        initialModel: model,
        ...options,
      }),
      preserveSiteComponentPanelsExports({ initialModel: model }),
      configExtensionsFileEmitter(model),
    ],
    experimental: {
      /*
        When a js chunk loads any other async js chunk,
        Vite also preloads the async chunk's dependencies.

        For example:
        in some source code:      `import('./some-other-file.js');`
        in `some-other-file.js`:  `import './some-stylesheet.css';`

        When Vite builds the above, the emitted dynamic import
        would also append a preload tag for the emitted css file

        Using `experimental.renderBuiltUrl` appends a JS expression as the baseUrl for this,
        Otherwise:
        - it would default to being relative to the page's origin
        - or we'd have to hardcode the `baseUrl` at build-time
      */
      renderBuiltUrl(filename, { hostType, hostId }) {
        // We use `includes` due to the hostId being relative on Windows
        if (hostType === 'html' && !hostId.includes('wix/html')) {
          return getEmbeddedScriptHtmlUrl(filename);
        }

        if (hostType !== 'js') {
          return { relative: true };
        }

        return {
          // https://github.com/wix-private/wix-cli/pull/900#discussion_r1201541691
          runtime: `import.meta.url.replace("${hostId}", "${filename}")`,
        };
      },
    },
  } satisfies InlineConfig;

  return mergeConfig(config, baseConfig);
}
